import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useToast } from '../Settings';
import { invalidatePermissionCache } from '../../hooks/usePermissions';
import {
  buildFlat, enableCap, disableCap, setScope, presetGrants, moduleSummary, countAll, sameGrants, describeAudit,
} from './accessLogic';

// ─────────────────────────────────────────────────────────────────────────────
// Settings → User Roles
//   • a list of the firm's roles (the Admin role is the only fixed one)
//   • a role editor: one tile per module; click a tile to open that module's
//     capabilities in a box, switch things on/off, choose "own" or "all" records
//   • changes are held as a draft and saved together
// The list of capabilities is NOT in this file — it is read from the server
// (GET /api/access/registry), so a new capability appears here automatically.
// ─────────────────────────────────────────────────────────────────────────────

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ICONS = {
  home: 'M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5zM9 21V12h6v9',
  users: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
  user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z',
  target: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 18a6 6 0 100-12 6 6 0 000 12zM12 14a2 2 0 100-4 2 2 0 000 4z',
  'check-square': 'M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11',
  layers: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  rupee: 'M6 3h12M6 8h12M6 13l8 8M6 13h3a4 4 0 000-8',
  book: 'M4 19.5A2.5 2.5 0 016.5 17H20M4 19.5A2.5 2.5 0 006.5 22H20V2H6.5A2.5 2.5 0 004 4.5v15z',
  refresh: 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15',
  briefcase: 'M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16',
  gift: 'M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z',
  'bar-chart': 'M18 20V10M12 20V4M6 20v-6',
  'shopping-bag': 'M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0',
  settings: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
};
const Svg = ({ name, size = 18, color = 'currentColor', sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    {(ICONS[name] || ICONS.layers).split('M').filter(Boolean).map((seg, i) => <path key={i} d={'M' + seg} />)}
  </svg>
);
const Lock = ({ size = 13, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
);

// A per-module accent so tiles are quick to tell apart without turning into a rainbow
const ACCENT = {
  dashboard: '#6366f1', clients: '#3b82f6', crm: '#f97316', tasks: '#10b981', services: '#8b5cf6', finance: '#16a34a',
  accounting: '#0ea5e9', renewals: '#f59e0b', people: '#ec4899', hr: '#14b8a6', referrals: '#d946ef', reports: '#6366f1',
  store: '#ef4444', settings: '#64748b',
};

function useApi(token) {
  return useCallback(async (method, path, body) => {
    const res = await fetch(`${BASE}/api${path}`, {
      method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let data = null;
    try { data = await res.json(); } catch { /* empty body */ }
    return { ok: res.ok, status: res.status, data };
  }, [token]);
}

// ── tiny UI atoms ────────────────────────────────────────────────────────────
function Switch({ on, onChange, disabled, size = 'md', label }) {
  const w = size === 'sm' ? 34 : 40, h = size === 'sm' ? 20 : 23, k = h - 6;
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(!on); }}
      style={{
        width: w, height: h, borderRadius: h, border: 'none', padding: 0, flexShrink: 0, position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer', background: on ? '#16a34a' : '#cbd5e1', opacity: disabled ? 0.55 : 1,
        transition: 'background .15s',
      }}>
      <span style={{ position: 'absolute', top: 3, left: on ? w - k - 3 : 3, width: k, height: k, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.25)', transition: 'left .15s' }} />
    </button>
  );
}

function Segmented({ value, options, onChange, disabled, T }) {
  return (
    <div style={{ display: 'inline-flex', background: T.bg3, border: `1px solid ${T.border2}`, borderRadius: 9, padding: 2, flexShrink: 0 }}>
      {options.map(o => {
        const active = value === o.value;
        return (
          <button key={o.value} type="button" disabled={disabled} title={o.title}
            onClick={() => onChange(o.value)}
            style={{
              padding: '4px 11px', fontSize: 11.5, fontWeight: 600, borderRadius: 7, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
              background: active ? '#0f1f45' : 'transparent', color: active ? '#fff' : T.text2, transition: 'all .12s',
            }}>{o.label}</button>
        );
      })}
    </div>
  );
}

const Pill = ({ children, bg, color, border }) => (
  <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: bg, color, border: `1px solid ${border || 'transparent'}`, whiteSpace: 'nowrap' }}>{children}</span>
);

function Avatar({ u, size = 26, T }) {
  const initials = (u.full_name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div title={u.full_name} style={{
      width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: `2px solid ${T.bg2}`,
      background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 700,
    }}>
      {u.avatar_url ? <img src={`${BASE}${u.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 20%' }} /> : initials}
    </div>
  );
}
const AvatarStack = ({ users, T, max = 4 }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    {users.slice(0, max).map((u, i) => <div key={u.id} style={{ marginLeft: i ? -8 : 0 }}><Avatar u={u} T={T} /></div>)}
    {users.length > max && <div style={{ marginLeft: -8, width: 26, height: 26, borderRadius: '50%', background: T.bg3, border: `2px solid ${T.bg2}`, fontSize: 10, fontWeight: 700, color: T.text2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+{users.length - max}</div>}
    {!users.length && <span style={{ fontSize: 12, color: T.text3 }}>No users yet</span>}
  </div>
);

function Modal({ children, onClose, width = 720, T, labelledBy }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-labelledby={labelledBy} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(15,31,69,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{
        width: `min(${width}px, 100%)`, maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', background: T.bg2,
        borderRadius: 18, boxShadow: '0 30px 80px rgba(0,0,0,0.35)', overflow: 'hidden', border: `1px solid ${T.border}`,
      }}>{children}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function RolesAccess({ token, darkMode = false, T: theme }) {
  const T = theme || { bg: '#f4f7ff', bg2: '#fff', bg3: '#f8faff', border: '#e8edf5', border2: '#e2e8f0', text: '#0f1f45', text2: '#4a5568', text3: '#94a3b8', cardBorder: '#f1f5f9', inputBg: '#fff', inputBorder: '#e2e8f0' };
  const api = useApi(token);
  const { showToast, showConfirm } = useToast();

  const [registry, setRegistry] = useState(null);
  const [roles, setRoles] = useState(null);
  const [openId, setOpenId] = useState(null);      // role being edited
  const [tab, setTab] = useState('roles');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const flat = useMemo(() => (registry ? buildFlat(registry) : {}), [registry]);

  const loadRoles = useCallback(async () => {
    const r = await api('GET', '/access/roles');
    if (!r.ok) { setError(r.data?.message || 'Could not load roles'); return; }
    setRoles(r.data);
  }, [api]);

  useEffect(() => {
    (async () => {
      const reg = await api('GET', '/access/registry');
      if (!reg.ok) { setError(reg.data?.message || 'Could not load permissions'); return; }
      setRegistry(reg.data.modules);
      await loadRoles();
    })();
  }, [api, loadRoles]);

  if (error) return <div style={{ padding: 30, color: '#dc2626', fontSize: 13 }}>{error}</div>;
  if (!registry || !roles) return <div style={{ padding: '60px 0', textAlign: 'center', color: T.text3, fontSize: 13 }}>Loading roles…</div>;

  if (openId) {
    return (
      <RoleEditor
        key={openId} roleId={openId} registry={registry} flat={flat} roles={roles} T={T} darkMode={darkMode} api={api}
        showToast={showToast} showConfirm={showConfirm}
        onBack={() => { setOpenId(null); loadRoles(); }}
        onChanged={loadRoles}
        onOpen={(id) => setOpenId(id)}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: T.text, margin: 0 }}>User Roles</h3>
          <p style={{ fontSize: 12.5, color: T.text3, margin: '4px 0 0', maxWidth: 560 }}>
            Create the roles your firm works with — Manager, Senior, Junior, Accountant, Intern… — and decide exactly what each can open and do.
          </p>
        </div>
        <button onClick={() => setCreating(true)} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          New role
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
        {[['roles', 'Roles'], ['log', 'Change log']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '9px 16px', fontSize: 13, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer',
            color: tab === k ? T.text : T.text3, borderBottom: `2px solid ${tab === k ? '#f97316' : 'transparent'}`, marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>

      {tab === 'roles'
        ? <RolesList roles={roles} total={Object.keys(flat).length} T={T} onOpen={setOpenId} />
        : <ChangeLog api={api} flat={flat} T={T} />}

      {creating && (
        <NewRoleDialog roles={roles} T={T} api={api} showToast={showToast} onClose={() => setCreating(false)}
          onCreated={async (role) => { setCreating(false); await loadRoles(); setOpenId(role.id); showToast(`Role “${role.name}” created — now choose what it can do`, 'success'); }} />
      )}
    </div>
  );
}

// ── Roles list ───────────────────────────────────────────────────────────────
function RolesList({ roles, total, T, onOpen }) {
  const [q, setQ] = useState('');
  const shown = roles.filter(r => r.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.2)', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
        <span style={{ color: '#2563eb', flexShrink: 0, marginTop: 1 }}><Lock size={15} /></span>
        <p style={{ margin: 0, fontSize: 12.5, color: T.text2, lineHeight: 1.55 }}>
          <b style={{ color: T.text }}>Admin</b> is the one built-in role: it always has full access and cannot be changed or removed.
          Every other role is yours to create, rename, edit or delete.
        </p>
      </div>

      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search roles…" aria-label="Search roles"
        style={{ width: 280, maxWidth: '100%', padding: '9px 13px', borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.text, fontSize: 13, outline: 'none', marginBottom: 14, boxSizing: 'border-box' }} />

      <div style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,1.2fr) minmax(160px,1.5fr) 150px 160px 28px', gap: 14, padding: '11px 20px', background: T.bg3, borderBottom: `1px solid ${T.border}`, fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <span>Role</span><span>About</span><span>Users</span><span>Access</span><span />
        </div>
        {shown.map(r => (
          <div key={r.id} role="button" tabIndex={0} onClick={() => onOpen(r.id)} onKeyDown={e => { if (e.key === 'Enter') onOpen(r.id); }}
            style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,1.2fr) minmax(160px,1.5fr) 150px 160px 28px', gap: 14, alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', transition: 'background .12s' }}
            onMouseEnter={e => { e.currentTarget.style.background = T.bg3; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: r.is_admin ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg3, color: r.is_admin ? '#fdba74' : T.text2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: r.is_admin ? 'none' : `1px solid ${T.border2}` }}>
                {r.is_admin ? <Lock size={15} /> : <Svg name="users" size={16} />}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#2563eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                {r.is_admin && <span style={{ fontSize: 10.5, fontWeight: 700, color: '#b45309' }}>BUILT-IN</span>}
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: T.text3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || (r.is_admin ? 'Full access to everything' : '—')}</div>
            <div><AvatarStack users={r.users || []} T={T} /></div>
            <div>
              <div style={{ fontSize: 12, color: T.text2, marginBottom: 5 }}>{r.is_admin ? 'Everything' : `${r.cap_count} of ${total} permissions`}</div>
              <div style={{ height: 4, borderRadius: 4, background: T.bg3, overflow: 'hidden' }}>
                <div style={{ height: 4, borderRadius: 4, width: `${r.is_admin ? 100 : Math.round((r.cap_count / total) * 100)}%`, background: r.is_admin ? '#f97316' : '#3b82f6' }} />
              </div>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2.2"><polyline points="9 18 15 12 9 6" /></svg>
          </div>
        ))}
        {!shown.length && <div style={{ padding: 36, textAlign: 'center', color: T.text3, fontSize: 13 }}>No roles match “{q}”.</div>}
      </div>
    </div>
  );
}

// ── New role dialog ──────────────────────────────────────────────────────────
function NewRoleDialog({ roles, T, api, showToast, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState('blank');
  const [copyId, setCopyId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const copyable = roles.filter(r => !r.is_admin);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Give the role a name'); return; }
    if (start === 'copy' && !copyId) { setErr('Choose the role to copy'); return; }
    setBusy(true); setErr('');
    const start_from = start === 'copy' ? `role:${copyId}` : start === 'blank' ? 'blank' : `template:${start}`;
    const r = await api('POST', '/access/roles', { name: name.trim(), description: description.trim(), start_from });
    setBusy(false);
    if (!r.ok) { setErr(r.data?.message || 'Could not create the role'); return; }
    onCreated(r.data);
  };

  const opt = (value, title, sub) => (
    <label key={value} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${start === value ? '#3b82f6' : T.border2}`, background: start === value ? 'rgba(59,130,246,0.06)' : 'transparent' }}>
      <input type="radio" name="start" checked={start === value} onChange={() => setStart(value)} style={{ marginTop: 3 }} />
      <span><span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.text }}>{title}</span><span style={{ fontSize: 11.5, color: T.text3 }}>{sub}</span></span>
    </label>
  );

  return (
    <Modal onClose={onClose} width={520} T={T} labelledBy="newrole-title">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '22px 26px 8px' }}>
          <h3 id="newrole-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: T.text }}>New role</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: T.text3 }}>You will choose what it can do on the next screen.</p>
        </div>
        <div style={{ padding: '14px 26px', overflowY: 'auto' }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Role name *</label>
          <input autoFocus value={name} onChange={e => { setName(e.target.value); setErr(''); }} maxLength={60} placeholder="e.g. Senior Associate"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.text, fontSize: 13.5, outline: 'none', marginBottom: 14 }} />
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Description <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this role for?"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.text, fontSize: 13, outline: 'none', marginBottom: 16 }} />
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Start from</label>
          <div style={{ display: 'grid', gap: 8 }}>
            {opt('blank', 'Blank role', 'Nothing switched on — add only what is needed.')}
            {opt('staff', 'Staff starting point', 'Works on their own clients, tasks and leads.')}
            {opt('manager', 'Manager starting point', 'Runs day-to-day work across the firm.')}
            {copyable.length > 0 && opt('copy', 'Copy an existing role', 'Same permissions as a role you already made.')}
          </div>
          {start === 'copy' && (
            <select value={copyId} onChange={e => setCopyId(e.target.value)} style={{ width: '100%', marginTop: 10, padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.text, fontSize: 13 }}>
              <option value="">Choose a role…</option>
              {copyable.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
          {err && <p role="alert" style={{ margin: '12px 0 0', fontSize: 12.5, color: '#dc2626' }}>{err}</p>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 26px 22px' }}>
          <button type="button" onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: `1px solid ${T.border2}`, background: 'transparent', color: T.text2, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button type="submit" disabled={busy} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? 'Creating…' : 'Create role'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ── Role editor ──────────────────────────────────────────────────────────────
function RoleEditor({ roleId, registry, flat, roles, T, darkMode, api, showToast, showConfirm, onBack, onChanged, onOpen }) {
  const [role, setRole] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [grants, setGrants] = useState({});
  const [saved, setSaved] = useState({ name: '', description: '', grants: {} });
  const [openModule, setOpenModule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState('');

  const listRow = roles.find(r => r.id === roleId);
  const isAdminRole = !!(role && role.is_admin);

  useEffect(() => {
    let live = true;
    (async () => {
      const r = await api('GET', `/access/roles/${roleId}`);
      if (!live) return;
      if (!r.ok) { setErr(r.data?.message || 'Could not load this role'); return; }
      setRole(r.data);
      setName(r.data.name); setDescription(r.data.description || '');
      const g = r.data.grants || {};
      setGrants(g); setSaved({ name: r.data.name, description: r.data.description || '', grants: g });
    })();
    return () => { live = false; };
  }, [api, roleId]);

  const dirty = role && !isAdminRole && (name.trim() !== saved.name || description.trim() !== saved.description || !sameGrants(grants, saved.grants));
  const counts = useMemo(() => countAll(registry, grants), [registry, grants]);
  const openModules = registry.filter(m => grants[m.access.key]).length;

  // warn before the tab closes with unsaved changes
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const back = async () => {
    if (dirty && !(await showConfirm('You have unsaved changes to this role. Leave without saving?'))) return;
    onBack();
  };

  const save = async () => {
    if (!name.trim()) { setErr('The role needs a name'); return; }
    setSaving(true); setErr('');
    try {
      if (name.trim() !== saved.name || description.trim() !== saved.description) {
        const r1 = await api('PUT', `/access/roles/${roleId}`, { name: name.trim(), description: description.trim() });
        if (!r1.ok) { setErr(r1.data?.message || 'Could not save the name'); setSaving(false); return; }
      }
      if (!sameGrants(grants, saved.grants)) {
        const r2 = await api('PUT', `/access/roles/${roleId}/capabilities`, { grants });
        if (!r2.ok) { setErr(r2.data?.message || 'Could not save permissions'); setSaving(false); return; }
        setGrants(r2.data.grants);
        setSaved({ name: name.trim(), description: description.trim(), grants: r2.data.grants });
      } else {
        setSaved(s => ({ ...s, name: name.trim(), description: description.trim() }));
      }
      invalidatePermissionCache();
      showToast('Role saved. People with this role get the change straight away.', 'success');
      onChanged();
    } finally { setSaving(false); }
  };

  const discard = () => { setName(saved.name); setDescription(saved.description); setGrants(saved.grants); setErr(''); };

  const duplicate = async () => {
    if (dirty && !(await showConfirm('Duplicate the SAVED version of this role? Your unsaved changes will not be copied.'))) return;
    const r = await api('POST', `/access/roles/${roleId}/duplicate`, {});
    if (!r.ok) { showToast(r.data?.message || 'Could not duplicate'); return; }
    showToast(`Created “${r.data.name}”`, 'success');
    await onChanged();
    onOpen(r.data.id);
  };

  if (err && !role) return <div><button onClick={onBack} style={linkBtn}>← Back</button><p style={{ color: '#dc2626', fontSize: 13 }}>{err}</p></div>;
  if (!role) return <div style={{ padding: '60px 0', textAlign: 'center', color: T.text3, fontSize: 13 }}>Loading role…</div>;

  const users = listRow?.users || [];
  const mod = openModule ? registry.find(m => m.key === openModule) : null;

  return (
    <div style={{ paddingBottom: dirty ? 84 : 0 }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <button onClick={back} aria-label="Back to roles" style={{ width: 34, height: 34, borderRadius: 10, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.07em' }}>User roles</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{isAdminRole ? 'Admin' : (name || 'Untitled role')}</div>
        </div>
        {!isAdminRole && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={duplicate} style={ghostBtn(T)}>Duplicate</button>
            <button onClick={() => setDeleting(true)} style={{ ...ghostBtn(T), color: '#dc2626', borderColor: 'rgba(220,38,38,.35)' }}>Delete</button>
          </div>
        )}
      </div>

      {isAdminRole && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', borderRadius: 14, padding: '16px 20px', margin: '16px 0' }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(249,115,22,.22)', color: '#fdba74', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Lock size={18} /></div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Admin always has full access</div>
            <div style={{ fontSize: 12.5, opacity: 0.75, marginTop: 2 }}>Every module, every action, on all records — including roles, users and billing. This role is built in and cannot be changed or deleted.</div>
          </div>
        </div>
      )}

      {/* name + description */}
      {!isAdminRole && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,1fr) minmax(260px,2fr)', gap: 14, margin: '16px 0 4px' }}>
          <div>
            <label style={labelStyle(T)}>Role name</label>
            <input value={name} onChange={e => { setName(e.target.value); setErr(''); }} maxLength={60} style={inputStyle(T)} />
          </div>
          <div>
            <label style={labelStyle(T)}>Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this role for?" style={inputStyle(T)} />
          </div>
        </div>
      )}
      {err && <p role="alert" style={{ margin: '8px 0', fontSize: 12.5, color: '#dc2626' }}>{err}</p>}

      {/* summary strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap', padding: '14px 18px', margin: '16px 0', background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 14 }}>
        <Stat label="Modules open" value={isAdminRole ? registry.length : openModules} of={registry.length} T={T} />
        <Stat label="Permissions on" value={isAdminRole ? counts.total : counts.on} of={counts.total} T={T} />
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: T.text3 }}>{users.length} user{users.length === 1 ? '' : 's'} with this role</span>
          <AvatarStack users={users} T={T} />
        </div>
      </div>

      <div style={{ fontSize: 12.5, color: T.text3, margin: '0 2px 12px' }}>
        {isAdminRole ? 'Everything is switched on.' : 'Pick a module to choose what this role can do in it. Modules that are off are hidden from these users completely.'}
      </div>

      {/* module tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14 }}>
        {registry.map(m => (
          <ModuleTile key={m.key} mod={m} grants={grants} locked={isAdminRole} T={T}
            onOpen={() => setOpenModule(m.key)}
            onToggle={(on) => {
              if (on) { setGrants(g => enableCap(g, flat, m.access.key).grants); setOpenModule(m.key); }
              else {
                const s = moduleSummary(m, grants);
                setGrants(g => disableCap(g, flat, m.access.key).grants);
                if (s.on) showToast(`${m.label} switched off — ${s.on} permission${s.on === 1 ? '' : 's'} cleared`, 'success');
              }
            }} />
        ))}
      </div>

      {/* sticky save bar */}
      {dirty && (
        <div style={{ position: 'sticky', bottom: 14, marginTop: 22, zIndex: 20, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#0f1f45', color: '#fff', borderRadius: 14, boxShadow: '0 14px 40px rgba(15,31,69,0.35)' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#f97316', flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>You have unsaved changes to “{name.trim() || 'this role'}”</span>
          <button onClick={discard} disabled={saving} style={{ padding: '8px 16px', borderRadius: 9, border: '1px solid rgba(255,255,255,.3)', background: 'transparent', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Discard</button>
          <button onClick={save} disabled={saving} style={{ padding: '8px 20px', borderRadius: 9, border: 'none', background: '#f97316', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      )}

      {mod && (
        <ModuleDialog key={mod.key} mod={mod} flat={flat} grants={grants} setGrants={setGrants} locked={isAdminRole} T={T}
          showToast={showToast} onClose={() => setOpenModule(null)} />
      )}

      {deleting && (
        <DeleteRoleDialog role={role} users={users} roles={roles} T={T} api={api} showToast={showToast}
          onClose={() => setDeleting(false)}
          onDeleted={async () => { invalidatePermissionCache(); showToast(`Role “${role.name}” deleted`, 'success'); await onChanged(); onBack(); }} />
      )}
    </div>
  );
}

const linkBtn = { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 };
const ghostBtn = (T) => ({ padding: '8px 16px', borderRadius: 10, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const labelStyle = (T) => ({ display: 'block', fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 });
const inputStyle = (T) => ({ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.text, fontSize: 13.5, outline: 'none' });

function Stat({ label, value, of, T }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>{value}<span style={{ fontSize: 13, fontWeight: 500, color: T.text3 }}> / {of}</span></div>
    </div>
  );
}

// ── One tile per module ──────────────────────────────────────────────────────
function ModuleTile({ mod, grants, locked, T, onOpen, onToggle }) {
  const s = moduleSummary(mod, grants);
  const open = locked || s.open;
  const accent = ACCENT[mod.key] || '#3b82f6';
  const pct = locked ? 100 : (s.total ? Math.round((s.on / s.total) * 100) : 0);
  const status = locked ? 'Full access' : !s.open ? 'Off' : s.on === 0 ? 'Open — nothing enabled yet' : `${s.on} of ${s.total} on`;
  return (
    <div role="button" tabIndex={0} aria-label={`${mod.label}: ${status}`}
      onClick={onOpen} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      style={{
        textAlign: 'left', cursor: 'pointer', background: T.bg2, borderRadius: 16, padding: '16px 16px 14px',
        border: `1.5px solid ${open ? accent + '66' : T.cardBorder}`, boxShadow: open ? `0 4px 16px ${accent}1f` : 'none',
        transition: 'all .15s', opacity: open ? 1 : 0.88, position: 'relative',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: open ? accent + '1f' : T.bg3, color: open ? accent : T.text3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Svg name={mod.icon} size={19} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{mod.label}</div>
          <div style={{ fontSize: 12, color: open ? T.text2 : T.text3, marginTop: 2 }}>{status}</div>
        </div>
        {!locked && <Switch on={s.open} onChange={onToggle} size="sm" label={`${mod.label} access`} />}
        {locked && <span style={{ color: '#b45309' }}><Lock size={14} /></span>}
      </div>
      <p style={{ fontSize: 11.5, color: T.text3, lineHeight: 1.45, margin: '10px 0 11px', minHeight: 32 }}>{mod.desc}</p>
      <div style={{ height: 4, borderRadius: 4, background: T.bg3 }}>
        <div style={{ height: 4, borderRadius: 4, width: `${pct}%`, background: accent, transition: 'width .25s' }} />
      </div>
      <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: open ? '#2563eb' : T.text3 }}>{locked ? 'View' : open ? 'Choose what they can do →' : 'Turn on to choose →'}</div>
    </div>
  );
}

// ── The box that opens for a module ──────────────────────────────────────────
function ModuleDialog({ mod, flat, grants, setGrants, locked, T, showToast, onClose }) {
  const [q, setQ] = useState('');
  const [hint, setHint] = useState('');
  const hintTimer = useRef(null);
  const accent = ACCENT[mod.key] || '#3b82f6';
  const s = moduleSummary(mod, grants);
  const open = locked || s.open;
  const showHint = (msg) => { setHint(msg); clearTimeout(hintTimer.current); hintTimer.current = setTimeout(() => setHint(''), 5000); };
  useEffect(() => () => clearTimeout(hintTimer.current), []);

  const toggle = (c, on) => {
    if (on) {
      const r = enableCap(grants, flat, c.key);
      setGrants(r.grants);
      if (r.alsoOn.length) showHint(`Also switched on: ${r.alsoOn.join(', ')} (needed for this)`);
    } else {
      const r = disableCap(grants, flat, c.key);
      setGrants(r.grants);
      if (r.alsoOff.length) showHint(`Also switched off: ${r.alsoOff.join(', ')} (they depend on this)`);
    }
  };

  const term = q.trim().toLowerCase();
  const groups = mod.groups
    .map(g => ({ ...g, caps: g.caps.filter(c => !term || c.label.toLowerCase().includes(term) || (c.desc || '').toLowerCase().includes(term)) }))
    .filter(g => g.caps.length);

  const preset = (kind) => {
    setGrants(g => presetGrants(mod, flat, g, kind));
    showHint(kind === 'all' ? `Everything in ${mod.label} switched on` : kind === 'none' ? `${mod.label} switched off` : `${mod.label} set to read-only`);
  };

  return (
    <Modal onClose={onClose} width={760} T={T} labelledBy="module-title">
      {/* header */}
      <div style={{ padding: '20px 26px 16px', borderBottom: `1px solid ${T.border}`, background: `linear-gradient(180deg, ${accent}14, transparent)` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 13, background: accent + '22', color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Svg name={mod.icon} size={21} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 id="module-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>{mod.label}</h3>
            <p style={{ margin: '3px 0 0', fontSize: 12.5, color: T.text3 }}>{mod.desc}</p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ width: 32, height: 32, borderRadius: 9, border: 'none', background: T.bg3, color: T.text2, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, padding: '11px 14px', borderRadius: 12, background: T.bg2, border: `1px solid ${open ? accent + '55' : T.border2}` }}>
          <Switch on={open} disabled={locked} onChange={(on) => {
            if (on) setGrants(g => enableCap(g, flat, mod.access.key).grants);
            else { setGrants(g => disableCap(g, flat, mod.access.key).grants); if (s.on) showHint(`${s.on} permission${s.on === 1 ? '' : 's'} cleared`); }
          }} label={`Open ${mod.label}`} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>{locked ? `${mod.label} is open with full access` : `Allow access to ${mod.label}`}</div>
            <div style={{ fontSize: 12, color: T.text3 }}>{open ? 'Choose below what they can do.' : `Off — ${mod.label} is hidden from this role entirely.`}</div>
          </div>
          {!locked && open && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => preset('view')} style={miniBtn(T)} title="Switch on only the view permissions">Read only</button>
              <button onClick={() => preset('all')} style={miniBtn(T)} title="Switch everything in this module on">Everything</button>
              <button onClick={() => preset('none')} style={miniBtn(T)} title="Switch this module off">Clear</button>
            </div>
          )}
        </div>
        {open && mod.groups.some(g => g.caps.some(c => c.scoped)) && (
          <p style={{ margin: '10px 2px 0', fontSize: 12, color: T.text3, lineHeight: 1.5 }}>
            <b style={{ color: T.text2 }}>Own</b> = {mod.ownLabel.toLowerCase()}. &nbsp;<b style={{ color: T.text2 }}>All</b> = {mod.allLabel.toLowerCase()}.
          </p>
        )}
      </div>

      {/* body */}
      <div style={{ padding: '4px 26px 8px', overflowY: 'auto', flex: 1, minHeight: 120, opacity: open ? 1 : 0.5, pointerEvents: open ? 'auto' : 'none' }}>
        {mod.groups.reduce((n, g) => n + g.caps.length, 0) > 9 && (
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search in ${mod.label}…`} aria-label="Search permissions"
            style={{ width: '100%', boxSizing: 'border-box', margin: '14px 0 4px', padding: '9px 13px', borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.text, fontSize: 13, outline: 'none' }} />
        )}
        {groups.map(g => (
          <div key={g.label} style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{g.label}</div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
              {g.caps.map((c, i) => {
                const val = locked ? (c.scoped ? 'all' : true) : grants[c.key];
                const on = !!val;
                const blocked = !!c.adminOnly;
                return (
                  <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderTop: i ? `1px solid ${T.border}` : 'none', background: on ? 'rgba(22,163,74,0.04)' : 'transparent' }}>
                    <Switch on={on || (blocked && locked)} disabled={locked || blocked} onChange={(v) => toggle(c, v)} size="sm" label={c.label} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{c.label}</span>
                        {c.sensitive && <Pill bg="rgba(245,158,11,.14)" color="#b45309" border="rgba(245,158,11,.4)">Sensitive</Pill>}
                        {blocked && <Pill bg="rgba(100,116,139,.14)" color={T.text2}>Admin only</Pill>}
                      </div>
                      {c.desc && <div style={{ fontSize: 11.5, color: T.text3, marginTop: 2, lineHeight: 1.4 }}>{c.desc}</div>}
                    </div>
                    {c.scoped && on && !locked && (
                      <Segmented T={T} value={val} onChange={(v) => setGrants(setScope(grants, flat, c.key, v))}
                        options={[{ value: 'own', label: 'Own', title: mod.ownLabel }, { value: 'all', label: 'All', title: mod.allLabel }]} />
                    )}
                    {c.scoped && locked && <Pill bg="rgba(22,163,74,.12)" color="#15803d">All</Pill>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {!groups.length && <p style={{ padding: '30px 0', textAlign: 'center', color: T.text3, fontSize: 13 }}>Nothing matches “{q}”.</p>}
        <div style={{ height: 14 }} />
      </div>

      {/* footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 26px', borderTop: `1px solid ${T.border}`, background: T.bg3 }}>
        <div style={{ flex: 1, fontSize: 12.5, color: hint ? '#b45309' : T.text3, minHeight: 18 }} aria-live="polite">
          {hint || (locked ? 'Admin cannot be changed.' : `${s.on} of ${s.total} permissions on in ${mod.label}. Changes are kept until you press Save on the role.`)}
        </div>
        <button onClick={onClose} style={{ padding: '9px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Done</button>
      </div>
    </Modal>
  );
}
const miniBtn = (T) => ({ padding: '6px 11px', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg3, color: T.text2, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' });

// ── Delete role ──────────────────────────────────────────────────────────────
function DeleteRoleDialog({ role, users, roles, T, api, showToast, onClose, onDeleted }) {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const targets = roles.filter(r => !r.is_admin && r.id !== role.id);
  const needMove = users.length > 0;
  const go = async () => {
    if (needMove && !to) { showToast('Choose a role to move these users to'); return; }
    setBusy(true);
    const r = await api('DELETE', `/access/roles/${role.id}${needMove ? `?reassign_to=${to}` : ''}`);
    setBusy(false);
    if (!r.ok) { showToast(r.data?.message || 'Could not delete the role'); return; }
    onDeleted();
  };
  return (
    <Modal onClose={onClose} width={460} T={T} labelledBy="delrole-title">
      <div style={{ padding: '24px 26px 8px' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6" /></svg>
        </div>
        <h3 id="delrole-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: T.text }}>Delete “{role.name}”?</h3>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: T.text2, lineHeight: 1.55 }}>
          {needMove
            ? `${users.length} user${users.length === 1 ? ' has' : 's have'} this role. They must move to another role first — they keep working with that role's access.`
            : 'No one has this role. It will be removed for good.'}
        </p>
        {needMove && (
          <select value={to} onChange={e => setTo(e.target.value)} style={{ width: '100%', marginTop: 14, padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.text, fontSize: 13 }}>
            <option value="">Move users to…</option>
            {targets.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 26px 22px' }}>
        <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: `1px solid ${T.border2}`, background: 'transparent', color: T.text2, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button onClick={go} disabled={busy} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? 'Deleting…' : needMove ? 'Move users and delete' : 'Delete role'}</button>
      </div>
    </Modal>
  );
}

// ── Change log ───────────────────────────────────────────────────────────────
function ChangeLog({ api, flat, T }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { (async () => { const r = await api('GET', '/access/audit'); setRows(r.ok ? r.data : []); })(); }, [api]);
  if (!rows) return <div style={{ padding: 40, textAlign: 'center', color: T.text3, fontSize: 13 }}>Loading…</div>;
  if (!rows.length) return <div style={{ padding: 40, textAlign: 'center', color: T.text3, fontSize: 13 }}>No changes recorded yet. Role and permission changes will appear here.</div>;
  return (
    <div style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
      {rows.map((e, i) => (
        <div key={e.id} style={{ display: 'flex', gap: 14, alignItems: 'baseline', padding: '12px 18px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
          <div style={{ width: 150, flexShrink: 0, fontSize: 12, color: T.text3 }}>{new Date(e.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
          <div style={{ flex: 1, fontSize: 13, color: T.text }}>{describeAudit(e, flat)}</div>
          <div style={{ fontSize: 12, color: T.text3 }}>{e.actor_name || 'Someone'}</div>
        </div>
      ))}
    </div>
  );
}
